import { useCallback, useEffect, useRef } from "react";
import { Play } from "lucide-react";
import { usePlayer } from "@/components/contexts/playerStore";

/**
 * The hole a page leaves for the global player.
 *
 * Renders no embed of its own: it reserves the layout space and tells the provider
 * "the player belongs here". GlobalVideoPlayer then animates its single fixed
 * iframe over this element's rect and keeps following it as the page scrolls.
 * Nobody else may render an embed - a second iframe is a second player.
 *
 * When the player is elsewhere (minimised over another route, or dismissed) the
 * slot shows the thumbnail with a play button, so the space is never a dead black
 * rectangle and starting again is one tap.
 *
 * `radius` travels through the DOM rather than context because the player reads
 * its target geometry inside an animation frame, where a React value would be a
 * render behind.
 */
export const PlayerSlot = ({ video, surface = "modal", className = "", radius = 0 }) => {
  const { play, registerSlot, mode, active, slot } = usePlayer();
  const ref = useRef(null);
  const videoRef = useRef(video);
  videoRef.current = video;

  const videoId = video?.id;

  useEffect(() => {
    if (videoRef.current) play(videoRef.current, { surface });
  }, [videoId, surface, play]);

  useEffect(() => registerSlot(ref.current), [registerSlot]);

  const start = useCallback(() => {
    if (videoRef.current) play(videoRef.current, { surface });
  }, [play, surface]);

  const filled = mode === "docked" && slot === ref.current && active?.id === videoId;

  return (
    <div
      ref={ref}
      data-tz-player-slot
      data-tz-slot-radius={radius}
      className={`relative overflow-hidden bg-black ${className}`}
    >
      {!filled && video && (
        <button
          onClick={start}
          className="absolute inset-0 w-full h-full group"
          aria-label={`Play ${video.title}`}
        >
          {video.thumbnail_high || video.thumbnail_url ? (
            <img
              src={video.thumbnail_high || video.thumbnail_url}
              alt=""
              className="w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity"
            />
          ) : null}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full bg-orange-500 text-black flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <Play className="w-6 h-6 ml-0.5 fill-current" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};
